import { mui } from "@/mui";
import { useMobyDB, useMobyNodeClient, useSession } from "./provider";
import { useLiveQuery } from 'dexie-react-hooks';
import { PhoneIcon, PhoneOffIcon, CheckIcon, XIcon, UsersIcon, PhoneIncomingIcon, MicIcon, MicOffIcon, VideoIcon, MonitorUp, CrownIcon } from "lucide-react";
import { useMediaSessions } from "@/hooks/useMediaSessions";
import { useIncomingCallSound } from "@/hooks/useSoundEffects";
import { useState, useEffect, useCallback, type ReactNode } from "react";
import { getPeerAvatarColor, renderPeerAvatarEmoji } from "@/lib/peer-profile-utils";
import { MessageDestinationType } from "@/moby/db";

interface StreamConnectionDrawerProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Drawer for initiating and managing A/V stream connections
 * Shows available peers with good connection health (green/yellow)
 * Handles incoming call notifications requiring user consent
 */
export function StreamConnectionDrawer({ children, open: controlledOpen, onOpenChange: controlledOnOpenChange }: StreamConnectionDrawerProps) {
  const session = useSession();
  const db = useMobyDB();
  const node = useMobyNodeClient();
  const mediaSessions = useMediaSessions();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const setIsOpen = (open: boolean) => {
    setUncontrolledOpen(open);
    controlledOnOpenChange?.(open);
  };
  const { startRinging, stopRinging } = useIncomingCallSound();
  
  // Component rendering - no logging to match contacts drawer
  
  // Force re-render when peers Map changes (not reactive otherwise)
  const [peersVersion, setPeersVersion] = useState(0);
  
  // GROUP REJOIN: Track group calls the user has left but are still active
  const [leftGroupCalls, setLeftGroupCalls] = useState<
    { sessionId: string; groupAddress: string; initiatorAddress: string; peerAddresses: string[]; leftAt: number }[]
  >([]);
  
  const refreshLeftGroupCalls = useCallback(() => {
    if (node.globalMediaRegistry) {
      const calls = node.globalMediaRegistry.getLeftGroupCalls();
      setLeftGroupCalls(Array.from(calls.values()));
    }
  }, [node]);
  
  // Refresh left group calls on mount and on relevant events
  useEffect(() => {
    refreshLeftGroupCalls();
    
    const handler = () => refreshLeftGroupCalls();
    window.addEventListener('av-participant-left', handler);
    window.addEventListener('av-group-call-ended', handler);
    window.addEventListener('av-call-ended', handler);
    
    return () => {
      window.removeEventListener('av-participant-left', handler);
      window.removeEventListener('av-group-call-ended', handler);
      window.removeEventListener('av-call-ended', handler);
    };
  }, [refreshLeftGroupCalls]);
  
  useEffect(() => {
    // Poll peers Map every 2 seconds to detect changes
    const interval = setInterval(() => {
      setPeersVersion(v => v + 1);
    }, 2000);
    
    return () => clearInterval(interval);
  }, []);
  
  // One-shot: switch to streams tab when a call first transitions to active
  const [lastAutoSwitchedSessionId, setLastAutoSwitchedSessionId] = useState<string | null>(null);
  useEffect(() => {
    const activeSessions = mediaSessions.filter(s => s.session.state === 'active');
    if (activeSessions.length > 0) {
      const firstActiveId = activeSessions[0].sessionId;
      if (firstActiveId !== lastAutoSwitchedSessionId) {
        setLastAutoSwitchedSessionId(firstActiveId);
        // Switch to streams tab once on first connection, but don't force-close drawer
        window.dispatchEvent(new CustomEvent('switch-to-streams-tab'));
      }
    }
  }, [mediaSessions, lastAutoSwitchedSessionId]);
  
  // Timer tick for active call durations displayed in the drawer
  const [timerTick, setTimerTick] = useState(0);
  useEffect(() => {
    const hasActive = mediaSessions.some(s => s.session.state === 'active');
    if (!hasActive) return;
    const interval = setInterval(() => setTimerTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [mediaSessions]);
  
  // Play incoming call sound when there are ringing calls (remote initiated, not yet answered)
  useEffect(() => {
    const ringingCalls = mediaSessions.filter(s => 
      !s.session.initiatedLocally && s.session.state === 'ringing'
    );
    
    if (ringingCalls.length > 0) {
      startRinging();
    } else {
      stopRinging();
    }
  }, [mediaSessions, startRinging, stopRinging]);
  
  // HOOKS FIX: useLiveQuery hooks must be called unconditionally (React rules of hooks).
  // They must appear ABOVE any conditional return to avoid React error #300 during logout.
  const availablePeers = useLiveQuery(async () => {
    if (!session) return [];
    try {
      const contacts = await db.contacts.toArray();
      
      // Query contacts from database
      
      // Filter for contacts that have connected peers in memory
      // AND don't have active sessions (those show in Incoming/Outgoing sections)
      const connected = contacts.filter(contact => {
        try {
          const peer = node.peers.get(contact.address);
          const isConnected = peer?.connection.connectionState === 'connected' || 
                             contact.connection?.state === 'connected';
          
          // Check if this peer has any active sessions
          const hasActiveSession = peer ? Array.from(peer.mediaSessions.values()).some(s =>
            s.state !== 'ended' && s.state !== 'idle'
          ) : false;
          
          // Check contact connection status
          
          // Only show in available if connected AND no active sessions
          return isConnected && !hasActiveSession;
        } catch (err) {
          console.error('[STREAM_DRAWER] Error checking contact', err);
          return false;
        }
      });
      
      // Filter complete
      
      return connected.map(contact => {
        const peer = node.peers.get(contact.address);
        
        return {
          address: contact.address,
          name: contact.getDisplayName(),
          initials: contact.getInitials(),
          healthScore: contact.connection?.healthScore || 'gray',
          connectionState: peer?.connection.connectionState || contact.connection?.state || 'new',
          profile: contact.custom?.profile // Remote peer profile for avatar/emoji
        };
      });
    } catch (err) {
      console.error('[STREAM_DRAWER] Error:', err);
      return [];
    }
  }, [peersVersion, session]); // Re-run when peers Map changes
  
  // Query available groups for group streaming
  const availableGroups = useLiveQuery(async () => {
    if (!session) return [];
    try {
      const chats = await db.chats.where('type').equals(MessageDestinationType.GROUP).toArray();
      
      // Filter groups that have at least one connected peer (besides self)
      const selfAddress = session.address;
      
      return chats.filter(chat => {
        // Check if any group peer is connected
        const otherPeers = chat.peers.filter(p => p !== selfAddress);
        const hasConnectedPeer = otherPeers.some(peerAddress => {
          const peer = node.peers.get(peerAddress);
          return peer?.connection.connectionState === 'connected';
        });
        return hasConnectedPeer;
      }).map(chat => ({
        address: chat.address,
        name: chat.name || `Group (${chat.peers.length + 1} members)`,
        peerCount: chat.peers.length,
        connectedCount: chat.peers.filter(p => {
          if (p === selfAddress) return false;
          const peer = node.peers.get(p);
          return peer?.connection.connectionState === 'connected';
        }).length,
        isOrganizer: !!chat.secret
      }));
    } catch (err) {
      console.error('[STREAM_DRAWER] Error querying groups:', err);
      return [];
    }
  }, [peersVersion, session]);

  if (session === undefined) {
    return null;
  }
  
  // Deduplicate sessions by sessionId (in case of polling duplicates)
  const uniqueSessions = Array.from(
    new Map(mediaSessions.map(s => [s.sessionId, s])).values()
  );
  
  // Deduplicate sessions
  
  // Get incoming call requests (calls where remote peer initiated)
  // Show all non-active incoming calls (ringing, connecting)
  const incomingCalls = uniqueSessions.filter(s => 
    !s.session.initiatedLocally && s.session.state !== 'active'
  );
  
  // Get outgoing call requests (calls where this peer initiated)
  // Show all non-active outgoing calls (offering, connecting)
  const outgoingCalls = uniqueSessions.filter(
    s => s.session.initiatedLocally && s.session.state !== 'active'
  );
  
  const handleInitiateCall = async (peerAddress: string) => {
    try {
      console.log('[STREAM_DRAWER] Initiating call', {
        peer: peerAddress.slice(0, 8)
      });
      
      const peer = node.getOrCreatePeer(peerAddress);
      if (peer.connection.connectionState !== 'connected') {
        console.error('[STREAM_DRAWER] Peer not connected');
        return;
      }
      
      // Check if there's already an active session with this peer
      const existingSessions = Array.from(peer.mediaSessions.values());
      const hasActiveSession = existingSessions.some(s => 
        s.state !== 'ended' && s.state !== 'idle'
      );
      
      if (hasActiveSession) {
        console.warn('[STREAM_DRAWER] Session already exists with this peer', {
          peer: peerAddress.slice(0, 8),
          sessions: existingSessions.map(s => ({
            id: s.sessionId.slice(0, 8),
            state: s.state
          }))
        });
        return;
      }
      
      // Start audio stream
      await peer.avStreamManager.startAudioStream([peerAddress]);
      
      console.log('[STREAM_DRAWER] Call initiated successfully');
    } catch (error) {
      console.error('[STREAM_DRAWER] Failed to initiate call', error);
    }
  };
  
  const handleAcceptCall = async (sessionId: string, peerAddress: string) => {
    try {
      console.log('[STREAM_DRAWER] Accepting call', {
        sessionId: sessionId.slice(0, 8),
        peer: peerAddress.slice(0, 8)
      });
      
      const peer = node.getOrCreatePeer(peerAddress);
      if (peer.connection.connectionState !== 'connected') {
        console.error('[STREAM_DRAWER] Peer not connected');
        return;
      }
      
      await peer.avStreamManager.acceptCall(sessionId);
      
      console.log('[STREAM_DRAWER] Call accepted');
    } catch (error) {
      console.error('[STREAM_DRAWER] Failed to accept call', error);
    }
  };
  
  const handleRejectCall = async (sessionId: string, peerAddress: string) => {
    try {
      console.log('[STREAM_DRAWER] Rejecting call', {
        sessionId: sessionId.slice(0, 8),
        peer: peerAddress.slice(0, 8)
      });
      
      const peer = node.getOrCreatePeer(peerAddress);
      if (peer.connection.connectionState !== 'connected') {
        console.error('[STREAM_DRAWER] Peer not connected');
        return;
      }
      
      await peer.avStreamManager.rejectCall(sessionId, 'user_rejected');
      
      console.log('[STREAM_DRAWER] Call rejected');
    } catch (error) {
      console.error('[STREAM_DRAWER] Failed to reject call', error);
    }
  };
  
  const handleCancelOutgoingCall = async (sessionId: string) => {
    try {
      console.log('[STREAM_DRAWER] Cancelling outgoing call', {
        sessionId: sessionId.slice(0, 8)
      });
      
      // Find any connected peer that has this session to access the AVStreamManager
      const peerWithSession = Array.from(node.peers.values()).find(
        p => p.mediaSessions.has(sessionId)
      );
      
      if (!peerWithSession) {
        console.error('[STREAM_DRAWER] No peer found with session for cancel');
        return;
      }
      
      await peerWithSession.avStreamManager.endAudioStream(sessionId, 'hangup');
      
      console.log('[STREAM_DRAWER] Outgoing call cancelled');
    } catch (error) {
      console.error('[STREAM_DRAWER] Failed to cancel outgoing call', error);
    }
  };
  
  // Handler for rejoining an active group call the user previously left
  const handleRejoinGroupCall = async (call: { sessionId: string; groupAddress: string; initiatorAddress: string; peerAddresses: string[] }) => {
    try {
      console.log('[STREAM_DRAWER] Rejoining group call', {
        group: call.groupAddress.slice(0, 8),
        sessionId: call.sessionId.slice(0, 8),
        initiator: call.initiatorAddress.slice(0, 8),
        peers: call.peerAddresses.map(p => p.slice(0, 8))
      });
      
      // Get any connected peer to access the AVStreamManager
      const connectedPeer = Array.from(node.peers.values()).find(
        p => p.connection.connectionState === 'connected'
      );
      
      if (!connectedPeer) {
        console.error('[STREAM_DRAWER] No connected peers available for rejoin');
        return;
      }
      
      // Use rejoinGroupAudioStream to reuse the original sessionId
      await connectedPeer.avStreamManager.rejoinGroupAudioStream(
        call.sessionId,
        call.groupAddress,
        call.initiatorAddress,
        call.peerAddresses
      );
      
      // REJOIN FIX: Clear the left-call tracking AFTER successful rejoin.
      // Previously cleared before the attempt — if rejoin failed (SDP race,
      // no connected peers, etc.), the Rejoin button vanished and the user
      // couldn't retry.
      if (node.globalMediaRegistry) {
        node.globalMediaRegistry.clearLeftGroupCall(call.sessionId);
      }
      refreshLeftGroupCalls();
      
      console.log('[STREAM_DRAWER] Group call rejoined successfully');
    } catch (error) {
      console.error('[STREAM_DRAWER] Failed to rejoin group call', error);
    }
  };
  
  // Handler for initiating group audio stream (organizer-only)
  const handleInitiateGroupCall = async (groupAddress: string, isOrganizer: boolean) => {
    if (!isOrganizer) {
      console.warn('[STREAM_DRAWER] Non-organizer attempted to initiate group call', {
        group: groupAddress.slice(0, 8)
      });
      return;
    }
    
    try {
      // Log all connected peers at initiation time
      const allConnectedPeers = Array.from(node.peers.entries())
        .filter(([_, p]) => p.connection.connectionState === 'connected')
        .map(([addr, _]) => addr.slice(0, 8));
      
      console.log('[STREAM_DRAWER] Initiating group call (organizer)', {
        group: groupAddress.slice(0, 8),
        connectedPeersAtInit: allConnectedPeers,
        totalConnected: allConnectedPeers.length
      });
      
      // Get any connected peer to access the AVStreamManager
      const connectedPeer = Array.from(node.peers.values()).find(
        p => p.connection.connectionState === 'connected'
      );
      
      if (!connectedPeer) {
        console.error('[STREAM_DRAWER] No connected peers available for group call');
        return;
      }
      
      console.log('[STREAM_DRAWER] Using peer for AVStreamManager', {
        peer: connectedPeer.address.slice(0, 8)
      });
      
      // Use the new startGroupAudioStream method
      await connectedPeer.avStreamManager.startGroupAudioStream(groupAddress);
      
      console.log('[STREAM_DRAWER] Group call initiated successfully');
    } catch (error) {
      console.error('[STREAM_DRAWER] Failed to initiate group call', error);
    }
  };
  
  return (
    <mui.Drawer.Root open={isOpen} onOpenChange={setIsOpen} direction="left">
      {controlledOpen === undefined ? (
        <mui.Drawer.Trigger asChild>
          {children}
        </mui.Drawer.Trigger>
      ) : (
        children
      )}
      <mui.Drawer.Portal>
        <mui.Drawer.Overlay className="sm:bg-transparent" />
        <mui.Drawer.Content className="bg-surface-container-high text-on-surface fixed inset-y-0 left-0 z-50 flex h-full w-full flex-col border-r border-outline sm:ml-20 sm:w-80 overflow-hidden">
          {/* Pointer arrow — visible on desktop */}
          <div className="hidden sm:block absolute -left-2 top-6 w-0 h-0 border-y-[8px] border-y-transparent border-r-[8px] border-r-surface-container-high" />
          <div className="border-b-outline flex items-center justify-between border-b p-4">
            <mui.Drawer.Title className="text-headline-sm">MobyStreams™</mui.Drawer.Title>
            <button onClick={() => setIsOpen(false)} className="sm:hidden p-1 rounded-full hover:bg-on-surface-variant/8" aria-label="Close drawer">
              <XIcon size={20} />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
          
          {/* Incoming Calls Section */}
          {incomingCalls.length > 0 && (
            <div>
              <h3 className="text-title-sm text-primary mb-3">Incoming Calls</h3>
              <div className="space-y-2">
                {incomingCalls.map(({ sessionId, session, peerAddress, contactName, contactInitials, isGroupCall, groupName, groupParticipantCount }) => (
                  <div
                    key={sessionId}
                    className="bg-surface-container-highest rounded-lg p-4"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      {isGroupCall ? (
                        <>
                          <div className="grid size-10 place-items-center rounded-full bg-tertiary text-on-tertiary">
                            <UsersIcon size={20} />
                          </div>
                          <div className="flex-1">
                            <div className="text-body-lg font-medium">{groupName || 'Group Call'}</div>
                            <div className="text-body-sm text-on-surface-variant">
                              {session.type === 0 ? 'Group audio call' : 'Group video call'}
                              {' · '}{(groupParticipantCount ?? 0) + 1} participants
                            </div>
                            <div className="text-body-sm text-on-surface-variant/70">
                              from {contactName}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div 
                            className="grid size-10 place-items-center rounded-full text-white"
                            style={{ backgroundColor: getPeerAvatarColor((incomingCalls.find(c => c.sessionId === sessionId) as any)?.contactProfile) }}
                          >
                            <div className="text-label-md">{contactInitials}</div>
                            {renderPeerAvatarEmoji((incomingCalls.find(c => c.sessionId === sessionId) as any)?.contactProfile, "sm")}
                          </div>
                          <div className="flex-1">
                            <div className="text-body-lg font-medium">{contactName}</div>
                            <div className="text-body-sm text-on-surface-variant">
                              {session.type === 0 ? 'Audio call' : 'Video call'}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAcceptCall(sessionId, peerAddress)}
                        className="flex-1 bg-tertiary text-on-tertiary hover:bg-tertiary/80 rounded-full py-2 px-4 flex items-center justify-center gap-2 transition-colors"
                      >
                        <CheckIcon size={18} />
                        Accept
                      </button>
                      <button
                        onClick={() => handleRejectCall(sessionId, peerAddress)}
                        className="flex-1 bg-error text-on-error hover:bg-error/80 rounded-full py-2 px-4 flex items-center justify-center gap-2 transition-colors"
                      >
                        <XIcon size={18} />
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Outgoing Calls Section */}
          {outgoingCalls.length > 0 && (
            <div>
              <h3 className="text-title-sm text-primary mb-3">Outgoing Calls</h3>
              <div className="space-y-2">
                {outgoingCalls.map(({ sessionId, session, peerAddress, contactName, contactInitials, isGroupCall, groupName, groupParticipantCount }) => (
                  <div
                    key={sessionId}
                    className="bg-surface-container-highest rounded-lg p-4 flex items-center gap-3"
                  >
                    {isGroupCall ? (
                      <>
                        <div className="grid size-10 place-items-center rounded-full bg-tertiary text-on-tertiary">
                          <UsersIcon size={20} />
                        </div>
                        <div className="flex-1">
                          <div className="text-body-lg font-medium">{groupName || 'Group Call'}</div>
                          <div className="text-body-sm text-on-surface-variant">
                            {session.state === 'offering' ? 'Calling...' : 'Connecting...'}
                            {' · '}{(groupParticipantCount ?? 0) + 1} participants
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div 
                          className="grid size-10 place-items-center rounded-full text-white"
                          style={{ backgroundColor: getPeerAvatarColor((outgoingCalls.find(c => c.sessionId === sessionId) as any)?.profile) }}
                        >
                          <div className="text-label-md">{contactInitials}</div>
                          {renderPeerAvatarEmoji((outgoingCalls.find(c => c.sessionId === sessionId) as any)?.profile, "sm")}
                        </div>
                        <div className="flex-1">
                          <div className="text-body-lg font-medium">{contactName}</div>
                          <div className="text-body-sm text-on-surface-variant">
                            {session.state === 'offering' ? 'Calling...' : 'Connecting...'}
                          </div>
                        </div>
                      </>
                    )}
                    <button
                      onClick={() => handleCancelOutgoingCall(sessionId)}
                      className="grid size-10 place-items-center rounded-full bg-error text-on-error hover:bg-error/80 transition-colors shrink-0"
                      title="Cancel call"
                    >
                      <PhoneOffIcon size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Rejoin Active Group Calls Section */}
          {leftGroupCalls.length > 0 && (
            <div className="mb-6">
              <h3 className="text-title-sm text-tertiary mb-3">Active Group Calls</h3>
              <div className="space-y-2">
                {leftGroupCalls.map((call) => (
                  <button
                    key={call.sessionId}
                    onClick={() => handleRejoinGroupCall(call)}
                    className="w-full bg-tertiary-container hover:bg-tertiary-container/80 rounded-lg p-4 flex items-center gap-3 transition-colors text-left"
                    title="Rejoin group call"
                  >
                    <div className="relative">
                      <div className="grid size-10 place-items-center rounded-full bg-tertiary text-on-tertiary">
                        <UsersIcon size={20} />
                      </div>
                      {/* Pulsing indicator for active call */}
                      <div className="absolute -top-0.5 -right-0.5 size-3 rounded-full bg-tertiary animate-pulse" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="text-body-lg font-medium text-on-tertiary-container">
                        Group Call In Progress
                      </div>
                      <div className="text-body-sm text-on-tertiary-container/70">
                        {call.peerAddresses.length} participant{call.peerAddresses.length !== 1 ? 's' : ''} remaining
                      </div>
                    </div>
                    
                    <PhoneIncomingIcon size={20} className="text-on-tertiary-container" />
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* ── Active Streams Section ── */}
          {(() => {
            const activeSessionsList = mediaSessions.filter(s => s.session.state === 'active');
            if (activeSessionsList.length === 0) return null;
            
            // Group by sessionId
            const groups = new Map<string, typeof activeSessionsList>();
            for (const s of activeSessionsList) {
              const arr = groups.get(s.sessionId) || [];
              arr.push(s);
              groups.set(s.sessionId, arr);
            }
            
            return (
              <div className="mb-4">
                <h3 className="text-title-sm text-tertiary mb-3 flex items-center gap-2">
                  <PhoneIcon size={14} />
                  Active Streams
                </h3>
                <div className="space-y-1">
                  {Array.from(groups.entries()).map(([sessionId, participants]) => {
                    const sess = participants[0].session;
                    const isModerator = sess.initiatorAddress === session.address;
                    
                    // Duration
                    const ms = sess.startTime ? Date.now() - sess.startTime : 0;
                    const min = Math.floor(ms / 60000);
                    const sec = Math.floor((ms % 60000) / 1000);
                    const dur = `${min}:${sec.toString().padStart(2, '0')}`;
                    
                    // STAR TOPOLOGY: For a leaf in a group call, useMediaSessions
                    // emits a single group-form entry (treeRole === 'leaf'). Render
                    // it as a group card rather than a peer row so the UI reflects
                    // "group call with N members" instead of "call with hub".
                    const firstEntry = participants[0];
                    const renderAsGroupCard = participants.length === 1
                      && firstEntry.isGroupCall
                      && !!firstEntry.groupName;

                    if (renderAsGroupCard) {
                      const memberCount = (firstEntry.groupParticipantCount ?? 0) + 1;
                      return (
                        <div key={sessionId} className="space-y-1">
                          <div className="bg-surface-container-highest rounded-lg px-3 py-2 flex items-center gap-2">
                            <div className="grid size-8 place-items-center rounded-full bg-tertiary text-on-tertiary">
                              <UsersIcon size={16} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-body-md font-medium truncate flex items-center gap-1">
                                {firstEntry.groupName}
                                {isModerator && <CrownIcon size={12} className="text-tertiary shrink-0" />}
                              </div>
                              <div className="text-label-sm text-on-surface-variant">
                                {memberCount} participants
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {sess.localStream?.getAudioTracks().some(t => t.enabled)
                                ? <MicIcon size={14} className="text-on-surface-variant" />
                                : <MicOffIcon size={14} className="text-error" />
                              }
                              {sess.videoEnabled && <VideoIcon size={14} className="text-primary" />}
                              {sess.screenShareEnabled && <MonitorUp size={14} className="text-tertiary" />}
                            </div>
                            <span className="text-label-sm text-on-surface-variant tabular-nums">{dur}</span>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={sessionId} className="space-y-1">
                        {/* Remote participants */}
                        {participants.map(({ peerAddress, contactName, contactInitials, contactProfile, session: peerSess }) => {
                          const isInitiator = peerSess.initiatorAddress === peerAddress;
                          const hasRemoteVideo = peerSess.remoteVideoEnabled?.get(peerAddress) ?? false;
                          const hasRemoteScreen = peerSess.remoteStreams.get(peerAddress)?.getVideoTracks().some(t => t.readyState === 'live' && t.label?.toLowerCase().includes('screen')) ?? false;
                          
                          return (
                            <div
                              key={peerAddress}
                              className="bg-surface-container-highest rounded-lg px-3 py-2 flex items-center gap-2"
                            >
                              <div className="relative">
                                <div
                                  className="grid size-8 place-items-center rounded-full text-white text-xs"
                                  style={{ backgroundColor: getPeerAvatarColor(contactProfile) }}
                                >
                                  {contactInitials}
                                  {renderPeerAvatarEmoji(contactProfile, "sm")}
                                </div>
                                <div className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-surface-container-high bg-green-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-body-md font-medium truncate flex items-center gap-1">
                                  {contactName}
                                  {isInitiator && <CrownIcon size={12} className="text-tertiary shrink-0" />}
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <MicIcon size={14} className="text-on-surface-variant" />
                                {hasRemoteVideo && <VideoIcon size={14} className="text-primary" />}
                                {hasRemoteScreen && <MonitorUp size={14} className="text-tertiary" />}
                              </div>
                              <span className="text-label-sm text-on-surface-variant tabular-nums">{dur}</span>
                            </div>
                          );
                        })}
                        
                        {/* Local user */}
                        <div className="bg-surface-container-highest rounded-lg px-3 py-2 flex items-center gap-2">
                          <div className="relative">
                            <div className="grid size-8 place-items-center rounded-full bg-primary text-on-primary text-xs">
                              {session.name.slice(0, 2).toUpperCase()}
                            </div>
                            <div className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-surface-container-high bg-green-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-body-md font-medium truncate flex items-center gap-1">
                              You
                              {isModerator && <CrownIcon size={12} className="text-tertiary shrink-0" />}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {sess.localStream?.getAudioTracks().some(t => t.enabled)
                              ? <MicIcon size={14} className="text-on-surface-variant" />
                              : <MicOffIcon size={14} className="text-error" />
                            }
                            {sess.videoEnabled && <VideoIcon size={14} className="text-primary" />}
                            {sess.screenShareEnabled && <MonitorUp size={14} className="text-tertiary" />}
                          </div>
                          <span className="text-label-sm text-on-surface-variant tabular-nums">{dur}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Open AV Panel button */}
                <button
                  onClick={() => {
                    setIsOpen(false);
                    window.dispatchEvent(new CustomEvent('av-stream-panel-open'));
                  }}
                  className="w-full mt-3 py-2 px-4 rounded-full bg-primary text-on-primary text-label-md font-medium hover:bg-primary/80 transition-colors"
                >
                  Open A/V Stream Panel
                </button>
              </div>
            );
          })()}
          
          {/* ── Separator ── */}
          {mediaSessions.some(s => s.session.state === 'active') && (
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-outline-variant" />
              <span className="text-label-sm text-on-surface-variant">Available Peers</span>
              <div className="flex-1 h-px bg-outline-variant" />
            </div>
          )}
          
          {/* Available Peers Section */}
          <div>
            <h3 className="text-title-sm text-primary mb-3">Available Contacts</h3>
            {(availablePeers ?? []).length === 0 ? (
              <p className="text-body-sm text-on-surface-variant text-center py-4">
                No contacts available for calls.
                <br />
                Add contacts with good connection quality.
              </p>
            ) : (
              <div className="space-y-2">
                {(availablePeers ?? []).map((peer) => (
                  <button
                    key={peer.address}
                    onClick={() => handleInitiateCall(peer.address)}
                    className="w-full bg-surface-container-highest hover:bg-surface-container-high rounded-lg p-4 flex items-center gap-3 transition-colors text-left"
                    title="Start audio call"
                  >
                    <div className="relative">
                      <div 
                        className="grid size-10 place-items-center rounded-full text-white"
                        style={{ backgroundColor: getPeerAvatarColor(peer.profile) }}
                      >
                        <div className="text-label-md">{peer.initials}</div>
                        {renderPeerAvatarEmoji(peer.profile, "sm")}
                      </div>
                      
                      {/* Connection health indicator */}
                      <div 
                        className="absolute -bottom-1 -right-1 size-3 rounded-full border-2 border-surface"
                        style={{
                          backgroundColor: 
                            peer.healthScore === 'green' ? '#4CAF50' :
                            peer.healthScore === 'yellow' ? '#FFC107' :
                            '#9E9E9E'
                        }}
                      />
                    </div>
                    
                    <div className="flex-1">
                      <div className="text-body-lg font-medium">{peer.name}</div>
                      <div className="text-body-sm text-on-surface-variant">
                        {peer.connectionState === 'connected' ? 'Available' : 'Connecting'}
                      </div>
                    </div>
                    
                    <PhoneIcon size={20} className="text-on-surface-variant" />
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Available Groups Section */}
          <div className="mt-6">
            <h3 className="text-title-sm text-primary mb-3">Available Groups</h3>
            {(availableGroups ?? []).length === 0 ? (
              <p className="text-body-sm text-on-surface-variant text-center py-4">
                No groups available for calls.
                <br />
                Create a group with connected contacts.
              </p>
            ) : (
              <div className="space-y-2">
                {(availableGroups ?? []).map((group) => (
                  <button
                    key={group.address}
                    onClick={() => handleInitiateGroupCall(group.address, group.isOrganizer)}
                    disabled={!group.isOrganizer}
                    className={`w-full rounded-lg p-4 flex items-center gap-3 transition-colors text-left ${
                      group.isOrganizer
                        ? 'bg-surface-container-highest hover:bg-surface-container-high'
                        : 'bg-surface-container-highest/50 opacity-60 cursor-not-allowed'
                    }`}
                    title={group.isOrganizer ? 'Start group audio call' : 'Only the group organizer can start calls'}
                  >
                    <div className="relative">
                      <div className="grid size-10 place-items-center rounded-full bg-tertiary text-on-tertiary">
                        <UsersIcon size={20} />
                      </div>
                      
                      {/* Connected peers indicator */}
                      <div className="absolute -bottom-1 -right-1 size-4 rounded-full border-2 border-surface bg-tertiary-container text-on-tertiary-container text-xs flex items-center justify-center font-medium">
                        {group.connectedCount}
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <div className="text-body-lg font-medium flex items-center gap-1">
                        {group.name}
                        {group.isOrganizer && <CrownIcon size={12} className="text-tertiary shrink-0" />}
                      </div>
                      <div className="text-body-sm text-on-surface-variant">
                        {group.isOrganizer
                          ? `${group.connectedCount}/${group.peerCount} members online`
                          : 'Only organizer can start calls'
                        }
                      </div>
                    </div>
                    
                    <PhoneIcon size={20} className="text-on-surface-variant" />
                  </button>
                ))}
              </div>
            )}
          </div>
          </div>
        </mui.Drawer.Content>
      </mui.Drawer.Portal>
    </mui.Drawer.Root>
  );
}

